/**
 * V2 Case Studies — /v2/case-studies   (REWRITE 2026-02-24)
 *
 * Four real named cases supplied by Michael (public-naming confirmed for the
 * three client cases; Kindra Health is Kindra's own product). Alternating
 * full-width rows: text one side, navy art card with the product screenshot
 * inset in a subtle device-chrome frame the other.
 *
 * NO invented metrics. Outcomes stay qualitative.
 */
import React from 'react';
import {
  V2, V2Layout, V2Hero, Section, H1, H2, Reveal, Eyebrow, SpectrumText,
  CTAPrimary, IconTile, CloseCTA,
} from './V2Layout';

const BRAND = '/brand/v2/cases';

// ---------------------------------------------------------------------------
// Page-local accent SVG icons (sit small in the top-left of each navy card)
// ---------------------------------------------------------------------------

const grad = (id, from, to) => (
  <defs>
    <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor={from} />
      <stop offset="100%" stopColor={to} />
    </linearGradient>
  </defs>
);

// Cloud/server core with orbiting nodes — Travel Trucks
const AccentServerOrbit = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" preserveAspectRatio="xMidYMid meet">
    {grad('cs-svo', '#EB258D', '#9156C4')}
    <rect x="10" y="13" width="12" height="9" rx="2" stroke="url(#cs-svo)" strokeWidth="2" />
    <circle cx="16" cy="17.5" r="1.4" fill="url(#cs-svo)" />
    <circle cx="5"  cy="9"    r="2"   stroke="url(#cs-svo)" strokeWidth="1.8" />
    <circle cx="27" cy="9"    r="2"   stroke="url(#cs-svo)" strokeWidth="1.8" />
    <circle cx="16" cy="4"    r="2"   stroke="url(#cs-svo)" strokeWidth="1.8" />
    <path d="M16 6.5v4.5M7 11l3 2.5M25 11l-3 2.5" stroke="url(#cs-svo)" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// Pipeline w/ integration nodes — ProCloud
const AccentPipeline = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" preserveAspectRatio="xMidYMid meet">
    {grad('cs-pip', '#4F8FD0', '#1FB6C1')}
    <circle cx="6"  cy="16" r="3"  stroke="url(#cs-pip)" strokeWidth="2" />
    <circle cx="16" cy="16" r="3"  stroke="url(#cs-pip)" strokeWidth="2" />
    <circle cx="26" cy="16" r="3"  stroke="url(#cs-pip)" strokeWidth="2" />
    <path d="M9 16h4M19 16h4" stroke="url(#cs-pip)" strokeWidth="2" strokeLinecap="round" />
    <rect x="2"  y="4"  width="8" height="4" rx="1" stroke="url(#cs-pip)" strokeWidth="1.6" />
    <rect x="22" y="4"  width="8" height="4" rx="1" stroke="url(#cs-pip)" strokeWidth="1.6" />
    <path d="M6 8v5M26 8v5" stroke="url(#cs-pip)" strokeWidth="1.6" strokeLinecap="round" strokeDasharray="2 2" />
  </svg>
);

// Dashboard frame with alert dot — Manchester Roofing
const AccentDashboard = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" preserveAspectRatio="xMidYMid meet">
    {grad('cs-dsh', '#34D1B0', '#1FB6C1')}
    <rect x="3" y="5" width="26" height="22" rx="2.5" stroke="url(#cs-dsh)" strokeWidth="2" />
    <path d="M3 11h26" stroke="url(#cs-dsh)" strokeWidth="2" />
    <rect x="6" y="14" width="9" height="9" rx="1.5" stroke="url(#cs-dsh)" strokeWidth="1.6" />
    <rect x="17" y="14" width="9" height="5" rx="1.5" stroke="url(#cs-dsh)" strokeWidth="1.6" />
    <rect x="17" y="20" width="9" height="3" rx="1.2" stroke="url(#cs-dsh)" strokeWidth="1.6" />
    <circle cx="26.5" cy="8" r="2.2" fill="#EB258D" />
  </svg>
);

// Heart with pulse line — Kindra Health
const AccentHeartPulse = () => (
  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" preserveAspectRatio="xMidYMid meet">
    {grad('cs-hp', '#9156C4', '#EB258D')}
    <path d="M16 26S5 19.7 5 12.9C5 9.1 8 6.2 11.5 6.2c2 0 3.8 1 4.5 2.5.7-1.5 2.5-2.5 4.5-2.5C24 6.2 27 9.1 27 12.9 27 19.7 16 26 16 26z"
      stroke="url(#cs-hp)" strokeWidth="2" strokeLinejoin="round" />
    <path d="M7 16h3l1.5-3 2 6 2-4 1.5 1h7" stroke="url(#cs-hp)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ---------------------------------------------------------------------------
// CASES
// ---------------------------------------------------------------------------

const CASES = [
  {
    key: 'travel-trucks',
    accent: '#EB258D',
    badge: 'Automotive accessories · Full build',
    name: 'Travel Trucks',
    who: 'Iveco truck accessories · Online & trade',
    Accent: AccentServerOrbit,
    image: `${BRAND}/travel-trucks-1.jpg`,
    imageAlt: "Travel Trucks online storefront — a product page for an automotive brake upgrade kit",
    chromeLabel: 'travel-trucks.com.au',
    lead:
      "A growing accessories business with work scattered across inboxes, spreadsheets and people's heads. We brought the whole operation together — and gave them their own AI, on their own infrastructure, fully under their control.",
    listHeading: 'What we built',
    list: [
      'Claude + Cowork rolled out so every staff member can collaborate on projects in one place',
      'Automated client intake and outreach — no lead left sitting',
      'Automated quoting, invoicing and follow-ups, end to end',
      'Their own private cloud server housing all AI and company data — full control, full ownership',
      'Custom agent skills, email monitoring and calendar monitoring running in the background',
      'CG Clearview — an on-site CFO with direct context, actions and clear paths forward',
      'A Shopify agent that builds and manages their online store',
    ],
    outcomes: ['Hours back every week', 'More money captured', 'Full data ownership'],
  },
  {
    key: 'procloud',
    accent: '#4F8FD0',
    badge: 'Signage & print · Full build',
    name: 'ProCloud Creative',
    who: 'Signage industry · Design, print & install',
    Accent: AccentPipeline,
    image: `${BRAND}/procloud-1.jpg`,
    imageAlt: "ProCloud's CG Clearview dashboard — the 'Ask Clearview' AI advisor answering a hiring question with real Xero numbers",
    chromeLabel: 'procloud · Ask Clearview',
    lead:
      "A busy signage operation drowning in manual quoting and scattered project tracking. We built custom software made for how the signage trade actually works — and wired their whole pipeline together.",
    listHeading: 'What we built',
    list: [
      'Marketing pipeline automation and a smooth, automated client flow',
      'Custom quoting and project software built specifically for the signage industry',
      'Linked straight into Xero and Monday CRM — one connected pipeline, no double-handling',
      'Automated intake, outreach, quoting, invoicing and follow-ups, end to end',
      'Their own private cloud server for all AI and company data — full ownership',
      'Claude + Cowork for the whole team, plus agent skills, email and calendar monitoring',
      'CG Clearview on-site CFO with direct context and clear next steps',
    ],
    outcomes: ['Quoting time slashed', 'Hours back every week', 'More money captured'],
  },
  {
    key: 'manchester',
    accent: '#34D1B0',
    badge: 'Roofing & construction · Full build',
    name: 'Manchester Roofing',
    who: 'Roofing contractor · Quotes, sites & trades',
    Accent: AccentDashboard,
    image: `${BRAND}/manchester-1.jpg`,
    imageAlt: "Manchester Roofing operations portal sign-in — the staff entry point to their custom dashboard",
    chromeLabel: 'portal.manchesterroofing.com.au',
    lead:
      "A roofing business juggling quotes, site inspections, suppliers and contractors across too many tools. We built a single custom dashboard that runs the whole operation — and quietly watches for the problems the owner can't see.",
    listHeading: 'What we built',
    list: [
      'A custom company dashboard running quotes, site inspections, invoices and ordering in one place',
      'Client communication and contractor communication, managed from the same hub',
      "Smart alerts that flag when something's not right — before it becomes the owner's problem",
      'Automated intake, outreach, quoting, invoicing and follow-ups, end to end',
      'Their own private cloud server for all AI and company data — full ownership',
      'Claude + Cowork for the team, agent skills, email and calendar monitoring',
      'CG Clearview on-site CFO with direct context and clear paths forward',
    ],
    outcomes: ['One dashboard, whole business', 'Problems caught early', 'Hours back & more money'],
  },
  {
    key: 'kindra-health',
    accent: '#9156C4',
    badge: 'Health & care · Built by Kindra',
    name: 'Kindra Health',
    who: 'A context-aware health companion',
    Accent: AccentHeartPulse,
    image: `${BRAND}/kindra-health-1.jpg`,
    imageAlt: "Kindra Health app — the 'Your health profile' Record tab showing conditions and medications.",
    chromeLabel: 'app.kindra.health',
    ownProduct: true,
    lead:
      "We don't just build for clients — we build our own. Kindra Health is a health companion that helps people genuinely understand their conditions, and supports them in the long gaps between appointments, in plain English.",
    listHeading: 'What it does',
    list: [
      'Helps people understand their conditions, with care and support between visits',
      'Handles doctor, specialist and hospital visits — capturing reports and explaining them in plain English',
      'Tracks your health over time, so the full picture is always there',
      'An education section to learn about your conditions, your medications, and how they interact in the real world',
      'Context-aware throughout — it remembers your history and meets you where you are',
    ],
    outcomes: ['Plain-English health', 'Support between visits', 'Built by Kindra'],
  },
];

// ---------------------------------------------------------------------------
// The navy "art card" — product screenshot inset in a subtle device frame.
// ---------------------------------------------------------------------------
const CaseArt = ({ c }) => {
  const { Accent } = c;
  return (
    <div
      className="rounded-3xl p-4 sm:p-5 relative overflow-hidden"
      style={{
        background: `${V2.navy} linear-gradient(140deg, ${c.accent}26, ${c.accent}08)`,
        border: `1px solid ${c.accent}33`,
        boxShadow: '0 28px 70px -24px rgba(16,8,56,.55)',
      }}
    >
      {/* Accent badge — top-left, sits on top of the device frame */}
      <div
        className="absolute z-10 flex items-center gap-2 rounded-full px-3 py-1.5"
        style={{
          top: 16, left: 16,
          background: 'rgba(16,8,56,0.66)',
          border: `1px solid ${c.accent}55`,
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <Accent />
        <span className="text-[11px] font-semibold uppercase" style={{ color: V2.icy, letterSpacing: '.12em' }}>
          {c.name}
        </span>
      </div>

      {/* Device frame — chrome bar + screenshot */}
      <div
        className="relative rounded-xl overflow-hidden"
        style={{
          background: '#fff',
          border: '1px solid rgba(220,240,245,.18)',
          boxShadow: '0 20px 50px -22px rgba(0,0,0,.6)',
        }}
      >
        {/* chrome bar — traffic-light dots only, no URL label (URLs not yet
            confirmed with clients; the screenshot itself is the proof). */}
        <div
          className="flex items-center gap-1.5 px-3"
          style={{
            height: 22,
            background: 'linear-gradient(180deg, #2a1b5a 0%, #1f1247 100%)',
            borderBottom: '1px solid rgba(220,240,245,.10)',
          }}
        >
          <span style={{ width: 9, height: 9, borderRadius: 999, background: '#ff5f57' }} />
          <span style={{ width: 9, height: 9, borderRadius: 999, background: '#febc2e' }} />
          <span style={{ width: 9, height: 9, borderRadius: 999, background: '#28c840' }} />
        </div>

        {/* screenshot */}
        <img
          src={c.image}
          alt={c.imageAlt}
          loading="lazy"
          className="block w-full h-auto"
          style={{ display: 'block' }}
          draggable={false}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// A single alternating row
// ---------------------------------------------------------------------------
const CaseRow = ({ c, flip }) => (
  <Reveal>
    <article
      className="grid grid-cols-1 lg:grid-cols-[1.05fr_1.15fr] gap-10 lg:gap-14 items-center py-14 lg:py-20"
      style={{ borderBottom: `1px solid ${V2.hair}` }}
    >
      {/* TEXT */}
      <div className={`order-2 ${flip ? 'lg:order-2' : 'lg:order-1'}`}>
        {c.ownProduct && (
          <span
            className="inline-flex items-center gap-2 mb-4 text-[11px] font-semibold uppercase"
            style={{
              padding: '6px 12px',
              borderRadius: 999,
              background: V2.spectrum,
              color: V2.icy,
              letterSpacing: '.18em',
              boxShadow: '0 8px 20px -10px rgba(145,86,196,.55)',
            }}
            data-testid={`v2-cs-own-flag-${c.key}`}
          >
            <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: V2.icy }} />
            Our own product
          </span>
        )}

        <span
          className="inline-flex items-center text-[11px] sm:text-[12px] font-semibold uppercase"
          style={{
            color: c.accent, letterSpacing: '.18em',
            background: `${c.accent}14`, padding: '6px 12px',
            borderRadius: 999, border: `1px solid ${c.accent}44`,
          }}
        >
          {c.badge}
        </span>

        <h2
          className="mt-5 font-display font-extrabold"
          style={{ fontSize: 'clamp(28px, 3.6vw, 42px)', lineHeight: 1.08, letterSpacing: '-0.025em', color: V2.navy }}
        >
          {c.name}
        </h2>

        <p className="mt-3 text-[13px] font-semibold uppercase" style={{ color: V2.muted, letterSpacing: '.14em' }}>
          {c.who}
        </p>

        <p className="mt-5 text-[16px] sm:text-[17px] leading-relaxed" style={{ color: V2.muted }}>
          {c.lead}
        </p>

        <p className="mt-7 text-[12px] font-semibold uppercase" style={{ color: V2.navy, letterSpacing: '.16em' }}>
          {c.listHeading}
        </p>
        <ul className="mt-3 space-y-3">
          {c.list.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-[15px]" style={{ color: V2.navy }}>
              <svg width="20" height="20" viewBox="0 0 20 20" className="mt-1 shrink-0" aria-hidden preserveAspectRatio="xMidYMid meet">
                <circle cx="10" cy="10" r="9" fill="none" stroke={c.accent} strokeWidth="1.4" opacity=".5" />
                <path d="M5.5 10.5l3 3 6-7" stroke={c.accent} strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{ lineHeight: 1.55 }}>{item}</span>
            </li>
          ))}
        </ul>

        <div className="mt-7 flex flex-wrap gap-2">
          {c.outcomes.map((o, i) => (
            <span
              key={i}
              className="inline-flex items-center text-[13px] font-medium"
              style={{
                color: c.accent,
                background: `${c.accent}10`,
                border: `1px solid ${c.accent}3a`,
                borderRadius: 999,
                padding: '7px 14px',
                letterSpacing: '.01em',
              }}
            >
              {o}
            </span>
          ))}
        </div>
      </div>

      {/* ART */}
      <div className={`order-1 ${flip ? 'lg:order-1' : 'lg:order-2'}`}>
        <CaseArt c={c} />
      </div>
    </article>
  </Reveal>
);

// ===========================================================================
export default function V2CaseStudiesPage() {
  return (
    <V2Layout>
      <V2Hero>
        <Reveal><Eyebrow tone="light">Case studies</Eyebrow></Reveal>
        <Reveal delay={100}>
          <H1 className="mt-6 mx-auto max-w-[24ch]">
            Real businesses. Real builds. <SpectrumText>Real results.</SpectrumText>
          </H1>
        </Reveal>
        <Reveal delay={200}>
          <p
            className="mt-6 mx-auto max-w-[60ch] text-[17px] sm:text-[19px]"
            style={{ color: 'rgba(220,240,245,0.82)', lineHeight: 1.55 }}
          >
            The actual work — full systems we've built for real Australian businesses, plus our own products.
          </p>
        </Reveal>
      </V2Hero>

      {/* Intro strip */}
      <Section tone="warm" className="text-center">
        <Reveal>
          <H2 className="mx-auto max-w-[26ch]">
            Not demos. Real systems, running real businesses.
          </H2>
        </Reveal>
        <Reveal delay={120}>
          <p className="mt-6 mx-auto max-w-[64ch] text-[16px] sm:text-[18px]" style={{ color: V2.muted, lineHeight: 1.65 }}>
            Each of these started the same way — a business flat out, leaking time and money. Each ended with a complete, custom system doing the heavy lifting.{' '}
            <strong style={{ color: V2.navy, fontWeight: 700 }}>
              All three owners got hours back and more money in the business.
            </strong>
          </p>
        </Reveal>
      </Section>

      {/* 4 alternating case rows */}
      <Section tone="white">
        {CASES.map((c, i) => (
          <React.Fragment key={c.key}>
            <CaseRow c={c} flip={i % 2 === 1} />
            {/* Mid-page CTA: after Manchester Roofing (index 2), before Kindra Health */}
            {i === 2 && (
              <Reveal>
                <div
                  className="my-10 sm:my-12 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row items-center justify-between gap-5"
                  style={{
                    background: V2.warm,
                    border: `1px solid ${V2.hair}`,
                    boxShadow: '0 14px 40px -28px rgba(16,8,56,.4)',
                  }}
                  data-testid="v2-cases-midpage-cta"
                >
                  <p
                    className="font-display font-extrabold text-center sm:text-left"
                    style={{
                      color: V2.navy,
                      fontSize: 'clamp(18px, 2.4vw, 22px)',
                      lineHeight: 1.2,
                      letterSpacing: '-0.018em',
                    }}
                  >
                    Want a build like this?
                  </p>
                  <CTAPrimary className="shrink-0">Book a free 30-min call →</CTAPrimary>
                </div>
              </Reveal>
            )}
          </React.Fragment>
        ))}
      </Section>

      <CloseCTA
        heading="Want a build like these for your business?"
        body="Every one started with a simple Leak Check. Book a free 30-minute call and we'll find where your business is leaking — then show you exactly what's worth building."
      />
    </V2Layout>
  );
}
